import React, { useState } from 'react';
import { 
    Input, 
    Button, 
    Dropdown, 
    Option,
    makeStyles,
    tokens
} from '@fluentui/react-components';
import { AlertWithUser, UpdateAlertRequest, comparisonOperators } from '../Types/alert';

interface AlertComponentProps {
    alert: AlertWithUser;
    onSave: (updatedAlert: UpdateAlertRequest) => void;
    onCancel: () => void;
}

const useStyles = makeStyles({
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        maxWidth: '600px',
        width: '100%',
        margin: '0 auto',
    },
    formRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    formRowInline: {
        display: 'flex',
        flexDirection: 'row',
        gap: '16px',
        alignItems: 'center',
    },
    label: {
        fontWeight: '600',
        fontSize: '14px',
        color: tokens.colorNeutralForeground1,
        display: 'block',
    },
    readonlyField: {
        backgroundColor: tokens.colorNeutralBackground2,
        color: tokens.colorNeutralForeground2,
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px',
    },
});

const AlertComponent: React.FC<AlertComponentProps> = ({ alert, onSave, onCancel }) => {
    const styles = useStyles();
    
    // State for form fields (excluding non-editable fields)
    const [formData, setFormData] = useState({
        AlertTitle: alert.AlertTitle || '',
        AlertDescription: alert.AlertDescription || '',
        NotificationEmails: alert.NotificationEmails || '',
        NotificationChannels: 'email', // Default to email, hidden from user
        RevealBiPayload_JSON: alert.RevealBiPayload_JSON || '',
        IsActive: true, // Default to true, hidden from user
        IsPublic: alert.IsPublic,
        ComparisonOperator: alert.ComparisonOperator || '',
        ThresholdValue: alert.ThresholdValue || '',
        AlertSensitivity: alert.AlertSensitivity || '',
        MetricName: alert.MetricName || '',
    });

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = () => {
        const updatedAlert: UpdateAlertRequest = {
            AlertId: alert.AlertId,
            AlertTitle: formData.AlertTitle,
            AlertDescription: formData.AlertDescription,
            NotificationEmails: formData.NotificationEmails,
            RevealBiPayload_JSON: formData.RevealBiPayload_JSON,
            IsActive: formData.IsActive,
            IsPublic: formData.IsPublic,
            ComparisonOperator: formData.ComparisonOperator as any,
            NotificationChannels: formData.NotificationChannels,
            ThresholdValue: formData.ThresholdValue ? Number(formData.ThresholdValue) : null,
            AlertSensitivity: formData.AlertSensitivity as any,
            MetricName: formData.MetricName || null,
        };
        
        onSave(updatedAlert);
    };

    const sensitivityOptions = [
        { value: 'threshold-crossed', label: 'Threshold Crossed' },
        { value: 'value-changed', label: 'Value Changed' },
        { value: 'one-per-day', label: 'One Per Day' },
    ];

    return (
        <div className={styles.formContainer}>
            {/* Alert Title */}
            <div className={styles.formRow}>
                <label className={styles.label} htmlFor="alert-title">Alert Title *</label>
                <Input
                    id="alert-title"
                    value={formData.AlertTitle}
                    onChange={(_, data) => handleInputChange('AlertTitle', data.value)}
                    placeholder="Enter alert title"
                    required
                />
            </div>

            {/* Alert Description */}
            <div className={styles.formRow}>
                <label className={styles.label} htmlFor="alert-description">Alert Description</label>
                <Input
                    id="alert-description"
                    value={formData.AlertDescription}
                    onChange={(_, data) => handleInputChange('AlertDescription', data.value)}
                    placeholder="Enter alert description"
                />
            </div>

            {/* Metric Name */}
            <div className={styles.formRow}>
                <label className={styles.label} htmlFor="metric-name">Visualization*</label>
                <Input
                    id="metric-name"
                    value={formData.MetricName}
                    onChange={(_, data) => handleInputChange('MetricName', data.value)}
                    required
                    readOnly
                    className={styles.readonlyField}
                />
            </div>

            {/* Notification Emails */}
            <div className={styles.formRow}>
                <label className={styles.label} htmlFor="notification-emails">Notification Emails (comma-separated) *</label>
                <Input
                    id="notification-emails"
                    value={formData.NotificationEmails}
                    onChange={(_, data) => handleInputChange('NotificationEmails', data.value)}
                    placeholder="Enter email addresses (comma-separated)"
                    required
                />
            </div>

            {/* Comparison Operator and Threshold Value */}
            <div className={styles.formRowInline}>
                <div style={{ flex: 1 }}>
                    <label className={styles.label} htmlFor="comparison-operator">Comparison Operator *</label>
                    <Dropdown
                        id="comparison-operator"
                        value={formData.ComparisonOperator ? comparisonOperators.find(option => option.value === formData.ComparisonOperator)?.label : 'Select operator'}
                        style={{ minWidth: '100px', width: '100%' }}
                        onOptionSelect={(_, data) => handleInputChange('ComparisonOperator', data.optionValue)}
                        placeholder="Select operator"
                    >
                        {comparisonOperators.map(option => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Dropdown>
                </div>
                <div style={{ flex: 1 }}>
                    <label className={styles.label} htmlFor="threshold-value">Threshold Value *</label>
                    <Input
                        id="threshold-value"
                        type="number"
                        value={`${formData.ThresholdValue ?? ''}`}
                        onChange={(_, data) => handleInputChange('ThresholdValue', data.value)}
                        placeholder="Enter threshold value"
                        required
                    />
                </div>

                {/* Alert Sensitivity */}
                <div style={{ flex: 1 }}>
                    <label className={styles.label} htmlFor="alert-sensitivity">Alert Sensitivity *</label>
                    <Dropdown
                        id="alert-sensitivity"
                        value={formData.AlertSensitivity ? sensitivityOptions.find(option => option.value === formData.AlertSensitivity)?.label : 'Select sensitivity'}
                        style={{ minWidth: '100px', width: '100%' }}
                        onOptionSelect={(_, data) => handleInputChange('AlertSensitivity', data.optionValue)}
                        placeholder="Select sensitivity"
                    >
                        {sensitivityOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Dropdown>
                </div>
            </div>


            {/* Status Switches - Hidden for now */}
            {/* <div className={styles.formRowInline}>
                <div>
                    <label className={styles.label}>Active</label>
                    <Switch
                        checked={formData.IsActive}
                        onChange={(_, data) => handleInputChange('IsActive', data.checked)}
                    />
                </div>
            </div> */}

            {/* Read-only fields - Hidden for now */}
            {/* <div className={styles.formRow}>
                <label className={styles.label}>Notification Type</label>
                <Input
                    value={formData.NotificationChannels}
                    readOnly
                    className={styles.readonlyField}
                />
            </div> */}

            {!!alert.CreatedBy && (
                <div className={styles.formRow}>
                    <label className={styles.label} htmlFor="creator">Creator</label>
                    <Input
                        id="creator"
                        value={alert.CreatedByName || `User ID: ${alert.CreatedBy}`}
                        readOnly
                        className={styles.readonlyField}
                    />
                </div>
            )}

            {/* Action Buttons */}
            <div className={styles.buttonContainer}>
                <Button appearance="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button appearance="primary" onClick={handleSave}>
                    Save Changes
                </Button>
            </div>
        </div>
    );
};

export default AlertComponent;
